import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/liability-insurance")({
  head: () => ({
    meta: [
      { title: "Liability Insurance (RCLI) — Sprinter Van Rentals" },
      {
        name: "description",
        content:
          "Renter's Contingent Liability Insurance (RCLI) provides primary liability protection during your rental period — affordable, compliant, and easy to activate online.",
      },
      { property: "og:title", content: "Liability Insurance (RCLI) — Sprinter Van Rentals" },
      {
        property: "og:description",
        content:
          "Primary liability coverage for renters without a personal auto policy. Statutory limits, $2,500 deductible, easy online activation.",
      },
    ],
    links: [{ rel: "canonical", href: "https://sprinter-van-rentals.com/liability-insurance" }],
  }),
  component: LiabilityInsurancePage,
});

function LiabilityInsurancePage() {
  return (
    <main className="min-h-screen bg-background">
      <article className="max-w-3xl mx-auto px-6 py-16">
        <Link
          to="/"
          className="text-xs uppercase tracking-[0.2em] text-primary hover:opacity-80"
        >
          ← Back
        </Link>

        <h1 className="font-display text-3xl sm:text-4xl font-bold text-secondary mt-6">
          Renter's Contingent Liability Insurance (RCLI)
        </h1>
        <p className="text-lg text-muted-foreground mt-3">
          Essential Liability Protection for Your Rental
        </p>

        <div className="prose prose-neutral max-w-none mt-10 space-y-5 text-foreground leading-relaxed">
          <p>
            RCLI provides vital liability coverage for renters who may not have a
            personal automobile policy or prefer to avoid using their existing
            insurance.
          </p>
          <p>
            Acting as the primary liability insurance during the rental period,
            RCLI protects renters against claims due to third-party damages or
            injuries, ensuring compliance with legal requirements while driving a
            rental vehicle.
          </p>
          <p>
            This coverage is especially valuable for international visitors or
            those without personal auto insurance, offering an easy and
            affordable way to meet liability requirements without relying on
            alternative coverage options.
          </p>

          <h2 className="font-display text-2xl font-bold text-secondary pt-6">
            How to Activate RCLI
          </h2>
          <p>
            Getting covered with RCLI is straightforward and can be done entirely
            online:
          </p>
          <ol className="list-decimal pl-6 space-y-2">
            <li>Select the RCLI coverage option during your booking process.</li>
            <li>Agree to the terms and conditions of the policy.</li>
            <li>Pay the premium prior to the start of your rental period.</li>
          </ol>
          <p>
            Once activated, coverage begins at the time specified on your rental
            agreement and complies with the statutory financial responsibility
            limits of the jurisdiction where the accident occurs.
          </p>

          <h2 className="font-display text-2xl font-bold text-secondary pt-6">
            Coverage Highlights
          </h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Statutory Limits:</strong> Coverage aligns with the minimum
              liability requirements of the location where the accident occurred.
            </li>
            <li>
              <strong>Deductible:</strong> $2,500 per claim, keeping premium
              costs affordable.
            </li>
          </ul>
          <p>
            RCLI provides renters with the essential liability protection they
            need to handle unforeseen incidents with confidence. This ensures
            peace of mind while keeping personal auto insurance policies
            unaffected in case of an accident.
          </p>

          <h2 className="font-display text-2xl font-bold text-secondary pt-6">
            Why Choose RCLI?
          </h2>
          <p>
            By choosing RCLI through CarRentalInsurance.com, you gain legally
            compliant liability coverage at a fraction of the cost compared to
            rental counter options. It's a simple, cost-effective way to ensure
            you're covered for third-party claims, no matter where your journey
            takes you.
          </p>
          <p className="font-semibold">
            Activate RCLI today and enjoy the freedom to explore, fully insured.
          </p>
        </div>
      </article>
    </main>
  );
}
